// /src/pages/account/ChangePassword.tsx
//
// ChangePassword
// - Lets the currently signed-in user update their own password.
// - Uses shared confirm modal (useConfirm) for "Cancel / discard changes".
// - Uses shared toast system (useSystemAlert) for all feedback (success/warn/error).
//
// UX notes
// - Shows lightweight password strength meter (UI-only heuristic).
// - Handles brute-force protection UX:
//   - If backend returns 429, the page enters a temporary "locked" state
//     and shows a countdown until the next allowed attempt.
// - If backend returns 401/403, we treat it as an expired session and redirect to login.
//
// Implementation notes
// - AuthRoute should normally prevent entering this page without a session,
//   but we still keep a guard for safety.
// - Page header slots reset is handled by PageHeader on route changes,
//   so this component does not manually clear slots on unmount.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useRecoilValue } from 'recoil';

import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Lock, Visibility, VisibilityOff } from '@mui/icons-material';

import { userState } from '@recoils/atoms';
import { changePassword } from '@apis/users';
import { tokenStorage } from '@utils/tokenStorage';

import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

type StrengthLevel = 0 | 1 | 2 | 3 | 4;

const normalize = (v?: string | null) => (v ?? '').trim();

/**
 * Lightweight password strength heuristic (UI-only).
 * - 0: empty
 * - 1: weak
 * - 2: fair
 * - 3: good
 * - 4: strong
 */
const getStrength = (pw: string): { level: StrengthLevel; label: string } => {
  const p = pw ?? '';
  if (!p) return { level: 0, label: 'Empty' };

  let score = 0;
  if (p.length >= 8) score += 1;
  if (p.length >= 12) score += 1;
  if (/[A-Z]/.test(p)) score += 1;
  if (/[a-z]/.test(p)) score += 1;
  if (/\d/.test(p)) score += 1;
  if (/[^A-Za-z0-9]/.test(p)) score += 1;

  if (score <= 2) return { level: 1, label: 'Weak' };
  if (score <= 3) return { level: 2, label: 'Fair' };
  if (score <= 4) return { level: 3, label: 'Good' };
  return { level: 4, label: 'Strong' };
};

const strengthToProgress = (level: StrengthLevel) => {
  switch (level) {
    case 0:
      return 0;
    case 1:
      return 25;
    case 2:
      return 50;
    case 3:
      return 75;
    default:
      return 100;
  }
};

const strengthToChip = (level: StrengthLevel) => {
  if (level === 4) return { color: 'success' as const, variant: 'filled' as const };
  if (level === 3) return { color: 'success' as const, variant: 'outlined' as const };
  if (level === 2) return { color: 'warning' as const, variant: 'outlined' as const };
  if (level === 1) return { color: 'error' as const, variant: 'outlined' as const };
  return { color: 'default' as const, variant: 'outlined' as const };
};

/**
 * Extracts useful information from Axios errors.
 * - retryAfter is used for 429 UX (if the backend provides it).
 */
const parseError = (err: unknown) => {
  const e = err as AxiosError<any>;
  const status = e?.response?.status;

  const retryAfterRaw =
    e?.response?.headers?.['retry-after'] ?? e?.response?.headers?.['Retry-After'];
  const retryAfter = Number(retryAfterRaw);

  const message =
    (e?.response?.data?.message as string) ||
    (e as any)?.message ||
    'Failed to change password. Please try again.';

  return {
    status,
    message: String(message),
    retryAfter: Number.isFinite(retryAfter) ? retryAfter : undefined,
  };
};

const ChangePassword = () => {
  const navigate = useNavigate();
  const user = useRecoilValue(userState);

  const confirm = useConfirm();
  const systemAlert = useSystemAlert();

  // -------------------- Form state --------------------
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Caps Lock hint (best-effort)
  const [capsLockOn, setCapsLockOn] = useState(false);

  // -------------------- UX state ----------------------
  const [saving, setSaving] = useState(false);

  /**
   * When lockedUntil is set, user cannot submit until that time.
   * Used to provide a friendly UX for 429 rate-limited responses.
   */
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);

  // -------------------- Derived state -----------------
  const strength = useMemo(() => getStrength(newPassword), [newPassword]);
  const progress = useMemo(() => strengthToProgress(strength.level), [strength.level]);
  const strengthChip = useMemo(() => strengthToChip(strength.level), [strength.level]);

  const newPwMismatch =
    normalize(newPassword).length > 0 &&
    normalize(confirmPassword).length > 0 &&
    newPassword !== confirmPassword;

  const isLocked = useMemo(() => {
    if (!lockedUntil) return false;
    return Date.now() < lockedUntil;
  }, [lockedUntil]);

  const lockRemainingSec = useMemo(() => {
    if (!lockedUntil) return 0;
    const diff = Math.ceil((lockedUntil - Date.now()) / 1000);
    return diff > 0 ? diff : 0;
  }, [lockedUntil]);

  /**
   * Re-render while locked so the countdown label updates.
   * (We do not mutate the timestamp; we just keep the component ticking.)
   */
  useEffect(() => {
    if (!isLocked) return;

    const t = window.setInterval(() => {
      setLockedUntil((prev) => prev);
    }, 500);

    return () => window.clearInterval(t);
  }, [isLocked]);

  const isFormValid = useMemo(() => {
    if (!user) return false;
    if (!normalize(currentPassword)) return false;
    if (!normalize(newPassword)) return false;
    if (!normalize(confirmPassword)) return false;
    if (newPassword !== confirmPassword) return false;

    // UI-side minimum requirement (backend may enforce stricter rules)
    if (newPassword.length < 8) return false;

    return true;
  }, [user, currentPassword, newPassword, confirmPassword]);

  const canSubmit = isFormValid && !saving && !isLocked;

  // -------------------- Helpers -----------------------
  const redirectToLoginExpired = () => {
    tokenStorage.clearAll();
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.replace(`/login?reason=session_expired&returnTo=${returnTo}`);
  };

  const handleKeyDownCaps = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const caps = (e as any)?.getModifierState?.('CapsLock') === true;
    setCapsLockOn(caps);
  };

  // -------------------- Handlers ----------------------
  const handleSubmit = async () => {
    if (!user) {
      systemAlert.error('No user session found. Please sign in again.');
      redirectToLoginExpired();
      return;
    }

    if (!canSubmit) {
      if (isLocked) {
        systemAlert.warning(`Too many attempts. Try again in ${lockRemainingSec}s.`);
        return;
      }
      systemAlert.info('Please fill in all required fields correctly.');
      return;
    }

    setSaving(true);

    try {
      await changePassword(Number(user.userId), {
        username: user.username,
        oldPassword: currentPassword,
        newPassword,
      });

      systemAlert.success('Password updated successfully.');

      // Clear inputs after success
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setCapsLockOn(false);
    } catch (err) {
      const { status, message, retryAfter } = parseError(err);

      // 401/403: treat as expired/invalid session
      if (status === 401 || status === 403) {
        systemAlert.warning('Your session has expired. Please sign in again.');
        redirectToLoginExpired();
        return;
      }

      // 429: rate limited (brute-force prevention UX)
      if (status === 429) {
        // Prefer server-provided Retry-After; fallback to 30s.
        const waitSec = retryAfter && retryAfter > 0 ? retryAfter : 30;
        setLockedUntil(Date.now() + waitSec * 1000);
        systemAlert.warning(`Too many attempts. Please wait ${waitSec}s and try again.`);
        return;
      }

      systemAlert.error(message || 'Failed to change password.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (saving) return;

    const hasAnyInput =
      normalize(currentPassword).length > 0 ||
      normalize(newPassword).length > 0 ||
      normalize(confirmPassword).length > 0;

    if (!hasAnyInput) {
      navigate(-1);
      return;
    }

    const ok = await confirm({
      priority: 'info',
      title: 'Discard changes?',
      description: 'Your input will be cleared and you will leave this page.',
      confirmText: 'Discard',
      cancelText: 'Stay',
      autoFocusConfirm: false,
    });

    if (ok) navigate(-1);
  };

  // Guard: AuthRoute should normally prevent this, but keep it safe.
  if (!user) {
    return (
      <Box p={2}>
        <Alert severity="error">No active session. Please sign in again.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2, maxWidth: 900, width: '100%' }}>
      <Stack spacing={3} sx={{ minWidth: 0 }}>
        {/* Main form card */}
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Stack spacing={2.5}>
            {/* Account context */}
            <Stack spacing={1}>
              <Typography variant="subtitle2" fontWeight={900}>
                Account
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                <Chip
                  icon={<Lock />}
                  label={`${user.name} (${user.username})`}
                  variant="outlined"
                  sx={{ fontWeight: 800, alignSelf: { xs: 'flex-start', sm: 'center' } }}
                />
                <Typography variant="caption" color="text.secondary">
                  If your session is expired, you’ll be asked to sign in again.
                </Typography>
              </Stack>
            </Stack>

            <Divider />

            {/* Lock / CapsLock banners */}
            {isLocked ? (
              <Alert severity="warning">
                Too many attempts. Please wait <b>{lockRemainingSec}s</b> and try again.
              </Alert>
            ) : null}

            {capsLockOn ? (
              <Alert severity="warning" sx={{ py: 0.75 }}>
                Caps Lock is on.
              </Alert>
            ) : null}

            {/* Password form */}
            <Stack spacing={2.25}>
              <TextField
                label="Current Password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                onKeyDown={handleKeyDownCaps}
                onBlur={() => setCapsLockOn(false)}
                type={showCurrent ? 'text' : 'password'}
                autoComplete="current-password"
                fullWidth
                disabled={saving || isLocked}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        aria-label={showCurrent ? 'Hide password' : 'Show password'}
                        onClick={() => setShowCurrent((v) => !v)}
                        onMouseDown={(e) => e.preventDefault()}
                        disabled={saving || isLocked}
                      >
                        {showCurrent ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onKeyDown={handleKeyDownCaps}
                onBlur={() => setCapsLockOn(false)}
                type={showNew ? 'text' : 'password'}
                autoComplete="new-password"
                fullWidth
                disabled={saving || isLocked}
                helperText="Minimum 8 characters. Use a mix of letters, numbers, and symbols."
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        aria-label={showNew ? 'Hide password' : 'Show password'}
                        onClick={() => setShowNew((v) => !v)}
                        onMouseDown={(e) => e.preventDefault()}
                        disabled={saving || isLocked}
                      >
                        {showNew ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Password strength (UI heuristic) */}
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
                    Password strength
                  </Typography>
                  <Chip
                    size="small"
                    label={strength.label}
                    color={strengthChip.color}
                    variant={strengthChip.variant}
                    sx={{ fontWeight: 900 }}
                  />
                </Stack>

                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 8,
                    borderRadius: 999,
                    bgcolor: 'action.hover',
                  }}
                />
              </Box>

              <TextField
                label="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyDown={handleKeyDownCaps}
                onBlur={() => setCapsLockOn(false)}
                type={showConfirm ? 'text' : 'password'}
                autoComplete="new-password"
                fullWidth
                disabled={saving || isLocked}
                error={newPwMismatch}
                helperText={newPwMismatch ? 'Passwords do not match.' : ' '}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        aria-label={showConfirm ? 'Hide password' : 'Show password'}
                        onClick={() => setShowConfirm((v) => !v)}
                        onMouseDown={(e) => e.preventDefault()}
                        disabled={saving || isLocked}
                      >
                        {showConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            <Divider />

            {/* Actions */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={saving}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!canSubmit}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {saving ? 'Updating…' : 'Update Password'}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* Security recommendations */}
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Stack spacing={1}>
            <Typography variant="h6" fontWeight={900}>
              Recommended next steps
            </Typography>
            <Typography variant="body2" color="text.secondary">
              After changing your password, we recommend the following for better security.
            </Typography>

            <Stack spacing={0.75} sx={{ mt: 1 }}>
              <Typography variant="body2">• Use a password manager to store it safely.</Typography>
              <Typography variant="body2">• Avoid reusing passwords across services.</Typography>
              <Typography variant="body2">
                • If you suspect unusual activity, log out and sign in again.
              </Typography>
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

export default ChangePassword;
